<script module lang="ts">
  import Layout from '../components/Layout.svelte'

  export const layout = (): { component: typeof Layout; props: { title: string } } => ({
    component: Layout,
    props: { title: 'Components' },
  })
</script>

<script lang="ts">
  import { faArrowRight, faLandmark, faCalendar, faNewspaper, faLanguage } from '@fortawesome/free-solid-svg-icons'
  import Icon from '../components/Icon.svelte'
  import Button from '../components/Button.svelte'
  import ButtonLink from '../components/ButtonLink.svelte'
  import MirroredIcon from '../components/MirroredIcon.svelte'
  import Input from '../components/Input.svelte'
  import Pagination from '../components/Pagination.svelte'
  import { page as inertiaPage } from '@inertiajs/svelte'
  import FeatureCard from '../components/FeatureCard.svelte'
  import PageCard from '../components/PageCard.svelte'
  import TagsBlock from '../components/blocks/TagsBlock.svelte'
  import PageCategoryProvider from '../components/PageCategoryProvider.svelte'
  import CopyableSwatch from '../components/CopyableSwatch.svelte'
  import { inlineStyle } from '../utils/inlineStyle'
  import SectionHeading from '../components/SectionHeading.svelte'
  import TagPill from '../components/TagPill.svelte'
  import Drawer from '../components/Drawer.svelte'
  import LanguageButtonLinks from '../components/LanguageButtonLinks.svelte'
  import TableOfContents from '../components/TableOfContents.svelte'
  import type { TagKey } from '../i18n'
  import type { SerializedPage, TableOfContentsEntry } from '../types'

  const SECTIONS = [
    { id: 'colors', label: 'Colors' },
    { id: 'spacing', label: 'Spacing' },
    { id: 'radius', label: 'Radius' },
    { id: 'typography', label: 'Typography' },
    { id: 'shadows', label: 'Shadows & borders' },
    { id: 'button', label: 'Button' },
    { id: 'button-link', label: 'ButtonLink' },
    { id: 'input', label: 'Input' },
    { id: 'pagination', label: 'Pagination' },
    { id: 'section-heading', label: 'SectionHeading' },
    { id: 'feature-card', label: 'FeatureCard' },
    { id: 'page-card', label: 'PageCard' },
    { id: 'tag-pill', label: 'TagPill' },
    { id: 'tags-block', label: 'TagsBlock' },
    { id: 'icon', label: 'Icon & MirroredIcon' },
    { id: 'drawer', label: 'Drawer' },
    { id: 'language-button-links', label: 'LanguageButtonLinks' },
    { id: 'table-of-contents', label: 'TableOfContents' },
  ]

  const TABLE_OF_CONTENTS_ENTRIES: TableOfContentsEntry[] = SECTIONS.map((section) => ({ id: section.id, title: section.label }))

  const COLOR_SCALES = ['blue', 'green', 'red', 'orange', 'gray']
  const COLOR_STEPS = [5, 10, 20, 30, 40, 50, 60, 70, 80, 90, 95]
  const BASE_COLOR_TOKENS = ['--color-bg', '--color-bg-muted', '--color-text', '--color-text-muted', '--color-border-1']
  const VARIANTS = ['brand', 'warning', 'success', 'danger', 'neutral'] as const
  const CATEGORY_VARIANTS = ['brand', 'warning', 'success', 'neutral'] as const
  const BUTTON_VARIANTS = VARIANTS
  const BUTTON_OUTLINE_VARIANTS = ['brand-outline', 'warning-outline', 'success-outline', 'danger-outline', 'neutral-outline'] as const
  const LINK_BUTTON_VARIANTS = ['button-brand', 'button-warning', 'button-success', 'button-danger', 'button-neutral'] as const

  const SPACE_TOKENS = Array.from({ length: 38 }, (_, i) => `--space-${i}`)
  const RADIUS_TOKENS = ['--radius-1', '--radius-2', '--radius-3', '--radius-4', '--radius-5', '--radius-6']
  const FONT_SIZE_TOKENS = ['--font-size-1', '--font-size-2', '--font-size-3', '--font-size-4', '--font-size-5', '--font-size-6', '--font-size-7', '--font-size-8']

  const PLACEHOLDER_IMAGE =
    'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect width="400" height="300" fill="%23ccc"/%3E%3C/svg%3E'

  const SAMPLE_TAGS: TagKey[] = [
    'unesco_heritage',
    'family_friendly',
  ]

  const SAMPLE_PAGE: SerializedPage = {
    id: '1',
    title: 'Fuggerei',
    category: 'sightseeing',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    description: 'Die älteste Sozialsiedlung der Welt – bewohnt seit 1521.',
    blocks: [
      { id: '0', type: 'PreviewImageBlock', image: { src: PLACEHOLDER_IMAGE, srcset: '', width: null, height: null }, altText: 'Platzhalter' },
      { id: '1', type: 'TagsBlock', tags: SAMPLE_TAGS },
    ],
  }

  // The pagination demo links to this page with `?page=N`.
  const paginationDemo = $derived({
    currentPage: Number(new URLSearchParams(inertiaPage.url.split('?')[1]).get('page') ?? 3),
    totalPages: 10,
    totalCount: 91,
    perPage: 9,
  })

  let inputValue = $state('')
  let numberValue = $state<number | undefined>(42)
  let drawerOpen = $state(false)
</script>

<div class="documentation">
  <aside class="documentation-navigation">
    <PageCategoryProvider category="sightseeing">
      <TableOfContents entries={TABLE_OF_CONTENTS_ENTRIES} />
    </PageCategoryProvider>
  </aside>

  <main class="documentation-content">
    <h1>Components &amp; Design Tokens</h1>
    <p class="documentation-intro">
      Living reference for the design tokens in <code class="documentation-intro-code">globals.css</code> and the reusable Svelte components in
      <code class="documentation-intro-code">frontend/components</code>. Only available when <code class="documentation-intro-code">Rails.env.local?</code>.
    </p>

    <h2 class="documentation-heading" id="colors">Colors</h2>
    <p class="documentation-hint">Click a swatch to copy its variable name.</p>
    <section class="block">
      <p class="block-label">Semantic variants</p>
      <div class="swatch-list">
        {#each VARIANTS as variant (variant)}
          <div class="swatch">
            <div class="swatch-pair">
              <CopyableSwatch name="--color-variant-{variant}" swatchStyle="background-color: var(--color-variant-{variant})" />
              <CopyableSwatch name="--color-variant-{variant}-muted" swatchStyle="background-color: var(--color-variant-{variant}-muted)" />
            </div>
            <span class="swatch-name">--color-variant-{variant}</span>
          </div>
        {/each}
      </div>

      <p class="block-label">Scales</p>
      {#each COLOR_SCALES as scale (scale)}
        <div class="scale-row">
          <span class="scale-row-name">{scale}</span>
          <div class="scale-row-steps">
            {#each COLOR_STEPS as step (step)}
              <CopyableSwatch
                size="sm"
                name="--color-{scale}-{step.toString().padStart(2, '0')}"
                swatchStyle="background-color: var(--color-{scale}-{step.toString().padStart(2, '0')})"
              />
            {/each}
          </div>
        </div>
      {/each}

      <p class="block-label">Base</p>
      <div class="swatch-list">
        {#each BASE_COLOR_TOKENS as token (token)}
          <div class="swatch">
            <CopyableSwatch name={token} swatchStyle="background-color: var({token})" />
            <span class="swatch-name">{token}</span>
          </div>
        {/each}
      </div>
    </section>

    <h2 class="documentation-heading" id="spacing">Spacing</h2>
    <section class="block">
      {#each SPACE_TOKENS as token (token)}
        <div class="space-row">
          <span class="space-row-name">{token}</span>
          <div class="space-row-bar" {@attach inlineStyle(`width: var(${token})`)}></div>
        </div>
      {/each}
    </section>

    <h2 class="documentation-heading" id="radius">Radius</h2>
    <section class="block">
      <div class="swatch-list">
        {#each RADIUS_TOKENS as token (token)}
          <div class="swatch">
            <div class="swatch-color -radius-demo" {@attach inlineStyle(`border-radius: var(${token})`)}></div>
            <span class="swatch-name">{token}</span>
          </div>
        {/each}
      </div>
    </section>

    <h2 class="documentation-heading" id="typography">Typography</h2>
    <section class="block">
      {#each FONT_SIZE_TOKENS as token (token)}
        <p class="font-size-sample" {@attach inlineStyle(`font-size: var(${token})`)}>{token} — Sehenswürdigkeiten in Augsburg</p>
      {/each}
    </section>

    <h2 class="documentation-heading" id="shadows">Shadows &amp; borders</h2>
    <section class="block">
      <div class="swatch-list">
        <div class="swatch">
          <div class="swatch-color -shadow-demo"></div>
          <span class="swatch-name">--box-shadow-1</span>
        </div>
        <div class="swatch">
          <div class="swatch-color -border-demo"></div>
          <span class="swatch-name">--border-style-1</span>
        </div>
      </div>
    </section>

    <h2 class="documentation-heading" id="button">Button</h2>
    <section class="block">
      <div class="row">
        {#each BUTTON_VARIANTS as variant (variant)}
          <Button {variant}>{variant}</Button>
        {/each}
      </div>
      <div class="row">
        {#each BUTTON_OUTLINE_VARIANTS as variant (variant)}
          <Button {variant}>{variant}</Button>
        {/each}
      </div>
      <pre class="documentation-code">{'<Button variant="brand">Label</Button>'}</pre>
    </section>

    <h2 class="documentation-heading" id="button-link">ButtonLink</h2>
    <section class="block">
      <div class="row">
        {#each LINK_BUTTON_VARIANTS as variant (variant)}
          <ButtonLink href="#button-link" {variant}>{variant}</ButtonLink>
        {/each}
      </div>
      <pre class="documentation-code">{'<ButtonLink href="/some/path" variant="button-brand">Label</ButtonLink>'}</pre>
    </section>

    <h2 class="documentation-heading" id="input">Input</h2>
    <section class="block">
      <div class="row -column">
        <Input type="text" bind:value={inputValue} placeholder="Text input">
          {#snippet start()}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" width="18" height="18"><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
          {/snippet}
          {#snippet end()}
            {#if inputValue}
              <button type="button" onclick={() => (inputValue = '')} aria-label="Clear">&times;</button>
            {/if}
          {/snippet}
        </Input>
        <Input type="number" bind:value={numberValue} placeholder="Number input" />
        <Input type="text" placeholder="No icons" />
      </div>
      <pre class="documentation-code">{'<Input type="text" bind:value={value} placeholder="...">\n  {#snippet start()}...{/snippet}\n  {#snippet end()}...{/snippet}\n</Input>'}</pre>
    </section>

    <h2 class="documentation-heading" id="pagination">Pagination</h2>
    <section class="block">
      <Pagination pagination={paginationDemo} pageHref={(pageNumber) => `?page=${pageNumber}#pagination`} />
      <pre class="documentation-code">{'<Pagination {pagination} pageHref={(pageNumber) => ...} />'}</pre>
    </section>

    <h2 class="documentation-heading" id="section-heading">SectionHeading</h2>
    <section class="block">
      {#each CATEGORY_VARIANTS as variant (variant)}
        <div class="section-heading-demo">
          <SectionHeading eyebrow="Eyebrow · {variant}" level="h2" {variant}>Sehenswürdigkeiten</SectionHeading>
        </div>
      {/each}
      <pre class="documentation-code">{'<SectionHeading eyebrow="Eyebrow" level="h2" variant="brand">Title</SectionHeading>'}</pre>
    </section>

    <h2 class="documentation-heading" id="feature-card">FeatureCard</h2>
    <section class="block">
      <div class="feature-card-grid">
        <FeatureCard variant="sightseeing" icon={faLandmark} eyebrow="Eyebrow" title="Sightseeing" lead="Lead text for this card.">
          <ButtonLink href="#feature-card" variant="button-brand-outline">Action{#snippet end()}<MirroredIcon icon={faArrowRight} />{/snippet}</ButtonLink>
        </FeatureCard>
        <FeatureCard variant="events" icon={faCalendar} eyebrow="Eyebrow" title="Events" lead="Lead text for this card.">
          <ButtonLink href="#feature-card" variant="button-warning-outline">Action{#snippet end()}<MirroredIcon icon={faArrowRight} />{/snippet}</ButtonLink>
        </FeatureCard>
        <FeatureCard variant="blog" icon={faNewspaper} eyebrow="Eyebrow" title="Blog" lead="Lead text for this card.">
          <ButtonLink href="#feature-card" variant="button-success-outline">Action{#snippet end()}<MirroredIcon icon={faArrowRight} />{/snippet}</ButtonLink>
        </FeatureCard>
        <FeatureCard variant="language" icon={faLanguage} eyebrow="Eyebrow" title="Language" lead="Lead text for this card.">
          <ButtonLink href="#feature-card" variant="button-danger-outline">Action{#snippet end()}<MirroredIcon icon={faArrowRight} />{/snippet}</ButtonLink>
        </FeatureCard>
      </div>
    </section>

    <h2 class="documentation-heading" id="page-card">PageCard</h2>
    <section class="block">
      <div class="page-card-grid">
        <PageCard page={SAMPLE_PAGE} category="sightseeing" />
        <PageCard page={{ ...SAMPLE_PAGE, id: '2' }} category="events" />
        <PageCard page={{ ...SAMPLE_PAGE, id: '3' }} category="blog" />
      </div>
    </section>

    <h2 class="documentation-heading" id="tag-pill">TagPill</h2>
    <section class="block">
      <ul class="row tag-pill-list">
        {#each CATEGORY_VARIANTS as variant (variant)}
          <TagPill text={variant} {variant} />
        {/each}
      </ul>
      <pre class="documentation-code">{'<ul>\n  <TagPill text="Label" variant="brand" />\n</ul>'}</pre>
    </section>

    <h2 class="documentation-heading" id="tags-block">TagsBlock</h2>
    <section class="block">
      <PageCategoryProvider category="sightseeing">
        <TagsBlock block={{ id: '1', type: 'TagsBlock', tags: SAMPLE_TAGS }} />
      </PageCategoryProvider>
    </section>

    <h2 class="documentation-heading" id="icon">Icon</h2>
    <section class="block">
      <div class="row">
        <Icon icon={faLandmark} />
        <Icon icon={faCalendar} />
        <Icon icon={faNewspaper} />
        <Icon icon={faLanguage} />
      </div>
      <p class="block-label">MirroredIcon — flips in right-to-left locales</p>
      <div class="row">
        <MirroredIcon icon={faArrowRight} />
        <span dir="rtl"><MirroredIcon icon={faArrowRight} /></span>
      </div>
      <pre class="documentation-code">{'<Icon icon={faLandmark} />\n<MirroredIcon icon={faArrowRight} />'}</pre>
    </section>

    <h2 class="documentation-heading" id="drawer">Drawer</h2>
    <section class="block">
      <button type="button" class="drawer-demo-trigger" onclick={() => (drawerOpen = true)}>Open drawer</button>
      <Drawer bind:open={drawerOpen} closeLabel="Close">
        {#snippet children({ close })}
          <p class="drawer-demo-text">Drawer content. Closes on the close button, a click outside or Escape.</p>
          <button type="button" class="drawer-demo-trigger" onclick={close}>Close from content</button>
        {/snippet}
      </Drawer>
      <pre class="documentation-code">{'<Drawer bind:open closeLabel="Close">\n  {#snippet children({ close })}...{/snippet}\n</Drawer>'}</pre>
    </section>

    <h2 class="documentation-heading" id="language-button-links">LanguageButtonLinks</h2>
    <section class="block">
      <div class="row">
        <LanguageButtonLinks />
      </div>
    </section>

    <h2 class="documentation-heading" id="table-of-contents">TableOfContents</h2>
    <p class="documentation-hint">The navigation of this page. Only shown on desktop and with at least four entries; the active entry follows the scroll position.</p>
    <section class="block">
      <pre class="documentation-code">{'<PageCategoryProvider category="sightseeing">\n  <TableOfContents entries={[{ id: \'colors\', title: \'Colors\' }]} />\n</PageCategoryProvider>'}</pre>
    </section>
  </main>
</div>

<style>
  .documentation {
    margin-top: var(--space-5);
  }

  @media (--desktop) {
    .documentation {
      display: grid;
      grid-template-columns: 190px minmax(0, 1fr);
      column-gap: var(--space-10);
    }
  }

  .documentation-content {
    min-width: 0;
  }

  .documentation-intro {
    color: var(--color-text-muted);
    max-width: 60ch;
  }

  .documentation-intro-code {
    background-color: var(--color-bg-muted);
    border-radius: var(--radius-1);
    padding: 0 var(--space-3);
  }

  .documentation-hint {
    font-size: var(--font-size-3);
    color: var(--color-text-muted);
    margin: var(--space-3) 0 0;
  }

  .documentation-heading {
    margin-top: var(--space-16);
    padding-top: var(--space-9);
    border-top: var(--border-style-1);
    scroll-margin-top: var(--space-9);
  }

  .documentation-code {
    background-color: var(--color-bg-muted);
    border-radius: var(--radius-2);
    padding: var(--space-6);
    font-size: var(--font-size-3);
    overflow-x: auto;
  }

  .block {
    margin-top: var(--space-7);
  }

  .block-label {
    font-size: var(--font-size-2);
    text-transform: uppercase;
    letter-spacing: var(--letter-spacing-4);
    font-weight: var(--font-weight-3);
    color: var(--color-text-muted);
    margin: var(--space-9) 0 var(--space-4);
  }

  .swatch-list {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-7);
  }

  .swatch {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .swatch-pair {
    display: flex;
    gap: var(--space-3);
  }

  .swatch-color {
    width: 64px;
    height: 64px;
    border-radius: var(--radius-2);
    display: flex;

    &.-radius-demo {
      background-color: var(--color-variant-brand);
    }

    /* Classes, not static style attributes: the Content Security Policy blocks those. */
    &.-shadow-demo {
      box-shadow: var(--box-shadow-1);
    }

    &.-border-demo {
      border: var(--border-style-1);
    }
  }

  .swatch-name {
    font-size: var(--font-size-2);
    color: var(--color-text-muted);
    font-family: monospace;
  }

  .scale-row {
    display: flex;
    align-items: center;
    gap: var(--space-6);
    margin-bottom: var(--space-3);
  }

  .scale-row-name {
    width: 48px;
    font-size: var(--font-size-3);
    color: var(--color-text-muted);
  }

  .scale-row-steps {
    display: flex;
    gap: var(--space-2);
  }

  .space-row {
    display: flex;
    align-items: center;
    gap: var(--space-6);
    margin-bottom: var(--space-2);
  }

  .space-row-name {
    width: 96px;
    font-size: var(--font-size-3);
    font-family: monospace;
    color: var(--color-text-muted);
  }

  .space-row-bar {
    height: 12px;
    background-color: var(--color-variant-brand-muted);
    border-radius: var(--radius-1);
  }

  .font-size-sample {
    margin-block: var(--space-3);
  }

  .row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-6);
    margin-bottom: var(--space-5);
  }

  .row.-column {
    flex-direction: column;
    align-items: stretch;
    max-width: 24rem;
  }

  .feature-card-grid,
  .page-card-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: var(--space-7);
  }

  .section-heading-demo {
    margin-bottom: var(--space-7);
  }

  .tag-pill-list {
    list-style: none;
    padding: 0;
  }

  .drawer-demo-trigger {
    font: inherit;
    padding: var(--space-3) var(--space-6);
    border: var(--border-style-1);
    border-radius: var(--radius-2);
    background-color: var(--color-bg);
    color: var(--color-text);
    cursor: pointer;
  }

  .drawer-demo-text {
    margin: 0 0 var(--space-6);
  }
</style>
